import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { documentsData, getCategories, filterDocuments } from '../data/documents/index.js';
import '../styles/DocumentationPage.css';

const DocumentationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  const [filteredDocs, setFilteredDocs] = useState(documentsData);
  const navigate = useNavigate();

  // Leer categoría inicial de URL params
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);

  // Obtener categorías únicas
  const categories = getCategories();

  // Filtrar documentos
  useEffect(() => {
    const filtered = filterDocuments(searchTerm, selectedCategory);
    setFilteredDocs(filtered);
  }, [searchTerm, selectedCategory]);

  // Función para cambiar categoría y actualizar URL
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'Todas') {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  const handleDocumentClick = (docId) => {
    navigate(`/docs/${docId}`);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'básico': return '#10b981';
      case 'intermedio': return '#f59e0b';
      case 'avanzado': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Mostrar mensaje cuando no hay documentos cargados
  if (documentsData.length === 0) {
    return (
      <div className="documentation-page">
        {/* Hero Section con Estado Vacío */}
        <section className="docs-hero-search">
          <div className="container">
            <motion.div 
              className="docs-hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1>Documentación</h1>
              <p>Todo lo que necesitas saber para aprovechar al máximo Runamatic</p>
            </motion.div>
          </div>
        </section>

        {/* Empty State */}
        <section className="docs-empty-state">
          <div className="container">
            <motion.div 
              className="empty-state-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="empty-state-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
              </div>
              <h2>Documentación en Preparación</h2>
              <p>
                Estamos preparando una completa documentación organizada por categorías para ayudarte 
                a aprovechar al máximo Runamatic. Pronto tendrás acceso a guías detalladas para:
              </p>
              <div className="categories-preview">
                <div className="categories-grid">
                  <div className="category-preview">
                    <h4>Comienzo</h4>
                    <p>Primeros pasos y configuración inicial</p>
                  </div>
                  <div className="category-preview">
                    <h4>Instagram</h4>
                    <p>Configuración y automatización de Instagram</p>
                  </div>
                  <div className="category-preview">
                    <h4>Facebook</h4>
                    <p>Integración con Facebook y Messenger</p>
                  </div>
                  <div className="category-preview">
                    <h4>WhatsApp</h4>
                    <p>Configuración y uso de WhatsApp Business</p>
                  </div>
                  <div className="category-preview">
                    <h4>Flujos</h4>
                    <p>Creación de conversaciones automatizadas</p>
                  </div>
                  <div className="category-preview">
                    <h4>Webchat</h4>
                    <p>Chat en tu sitio web</p>
                  </div>
                  <div className="category-preview">
                    <h4>Navegación</h4>
                    <p>Cómo usar la plataforma</p>
                  </div>
                  <div className="category-preview">
                    <h4>Inteligencia Artificial</h4>
                    <p>Configuración y optimización de IA</p>
                  </div>
                  <div className="category-preview">
                    <h4>Integraciones</h4>
                    <p>Conecta con tus herramientas favoritas</p>
                  </div>
                  <div className="category-preview">
                    <h4>General</h4>
                    <p>Configuraciones y ajustes generales</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="documentation-page">
      {/* Hero y Search Section Unificado */}
      <section className="docs-hero-search">
        <div className="container">
          <motion.div 
            className="docs-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Documentación</h1>
            <p>Todo lo que necesitas saber para aprovechar al máximo Runamatic</p>
          </motion.div>

          <div className="search-filter-container">
            <motion.div 
              className="search-box"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="search-input-wrapper">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  type="text"
                  placeholder="Buscar en la documentación..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchTerm('')}
                  >
                    ×
                  </button>
                )}
              </div>
            </motion.div>

            <motion.div 
              className="category-filter"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {categories.map((category, index) => (
                <button
                  key={category}
                  className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category)}
                >
                  {category}
                </button>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Documents Grid */}
      <section className="docs-grid-section">
        <div className="container">
          <motion.div 
            className="docs-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {filteredDocs.length > 0 ? (
              filteredDocs.map((doc, index) => (
                <motion.div
                  key={doc.id}
                  className="doc-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  onClick={() => handleDocumentClick(doc.id)}
                >
                  <div className="doc-card-content">
                    <div className="doc-header">
                      <div className="doc-category">{doc.category}</div>
                      {doc.difficulty && (
                        <span 
                          className="doc-difficulty-badge"
                          style={{ backgroundColor: getDifficultyColor(doc.difficulty) }}
                        >
                          {doc.difficulty}
                        </span>
                      )}
                    </div>
                    <h3 className="doc-title">{doc.title}</h3>
                    <p className="doc-description">{doc.description}</p>
                    
                    {doc.lastUpdated && (
                      <div className="doc-metadata">
                        <span className="doc-last-updated">
                          {new Date(doc.lastUpdated).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    )}
                    
                    {doc.tags && doc.tags.length > 0 && (
                      <div className="doc-tags">
                        {doc.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="doc-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="doc-card-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M7 17L17 7"></path>
                      <path d="M7 7h10v10"></path>
                    </svg>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div 
                className="no-results"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h3>No se encontraron documentos</h3>
                <p>Prueba con otros términos de búsqueda o selecciona una categoría diferente.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setSearchTerm('');
                    handleCategoryChange('Todas');
                  }}
                >
                  Limpiar filtros
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DocumentationPage; 